import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
export interface MenuAction {
  label: string;
  run?: () => void;
  disabled?: boolean;
  shortcut?: string;
}
export function Dropdown({
  label,
  actions,
  ariaLabel,
}: {
  ariaLabel?: string;
  label: ReactNode;
  actions: MenuAction[];
}) {
  const ref = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    const close = (e: KeyboardEvent) => {
      if (e.key === "Escape" && ref.current?.open) {
        ref.current.open = false;
        ref.current.querySelector("summary")?.focus();
      }
    };
    const outside = (e: PointerEvent) => {
      if (ref.current?.open && !ref.current.contains(e.target as Node))
        ref.current.open = false;
    };
    const dismiss = () => { if (ref.current) ref.current.open = false };
    window.addEventListener("resize", dismiss);
    document.addEventListener("keydown", close);
    document.addEventListener("pointerdown", outside);
    return () => {
      window.removeEventListener("resize", dismiss);
      document.removeEventListener("keydown", close);
      document.removeEventListener("pointerdown", outside);
    };
  }, []);
  return (
    <details
      className="nb-dropdown"
      ref={ref}
      onToggle={() => {
        const details = ref.current;
        if (!details?.open || !details.closest(".nb-tree-row,.ide-tree-row,.ide-explorer-title,.nb-equation-galleries")) return;
        const menu = details.querySelector<HTMLElement>(".nb-dropdown-menu");
        const summary = details.querySelector("summary");
        if (menu && summary) {
          const rect = summary.getBoundingClientRect();
          menu.style.top = `${Math.max(8, Math.min(rect.bottom + 6, window.innerHeight - menu.offsetHeight - 8))}px`;
          const explorer = details.closest(".ide-explorer-title")?.getBoundingClientRect();
          const left = explorer ? Math.max(explorer.left + 8, rect.right - menu.offsetWidth) : rect.left;
          menu.style.left = `${Math.max(8, Math.min(left, window.innerWidth - menu.offsetWidth - 8))}px`;
        }
      }}
    >
      <summary aria-label={ariaLabel} onMouseDown={(e) => e.preventDefault()}>{label}</summary>
      <div
        role="menu"
        className="nb-dropdown-menu"
        onKeyDown={(e) => {
          if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
          e.preventDefault();
          const items = Array.from(
            e.currentTarget.querySelectorAll<HTMLButtonElement>(
              "button:not(:disabled)",
            ),
          );
          const i = items.indexOf(document.activeElement as HTMLButtonElement);
          items[
            (i + (e.key === "ArrowDown" ? 1 : -1) + items.length) % items.length
          ]?.focus();
        }}
      >
        {actions.map((a, i) =>
          a.label === "—" ? (
            <hr key={i} />
          ) : (
            <button
              role="menuitem"
              key={`${a.label}-${i}`}
              disabled={a.disabled}
              onClick={() => {
                if (ref.current) ref.current.open = false;
                a.run?.();
              }}
            >
              <span>{a.label}</span>
              {a.shortcut && <small>{a.shortcut}</small>}
            </button>
          ),
        )}
      </div>
    </details>
  );
}
